/**
 * Multi-org membership, web side. The model and who writes what are in
 * functions/src/memberships/memberships.ts: users/{uid}/memberships/{orgId}
 * is one org this login belongs to, and users/{uid}'s orgId/role/officeId/
 * departmentId are the org it is working in now (only server code changes
 * them; the switch goes through orgSwitchRequests).
 */
import { addDoc, collection, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { type UserProfile, type UserRole } from '@/types/team';

export interface Membership {
    uid: string;
    orgId: string;
    orgName?: string;
    role: UserRole;
    officeId: string | null;
    departmentId: string | null;
    status: 'active' | 'inactive';
}

export function isActiveMembership(m: Pick<Membership, 'status'>): boolean {
    return m.status !== 'inactive';
}

export function membershipPath(uid: string, orgId: string): string {
    return `users/${uid}/memberships/${orgId}`;
}

/**
 * The org's roster: users working in the org now (their users doc is the
 * record), plus members whose active org is elsewhere, whose role, office,
 * department and status in THIS org come from their membership. Those carry
 * the client-only crossOrgMember marker, so edits go to the membership.
 * A cross-org member whose users doc could not be read is left out.
 */
export function mergeCrossOrgMembers(
    sameOrg: UserProfile[],
    memberships: Membership[],
    profilesByUid: Record<string, UserProfile | undefined>,
    orgId: string,
): UserProfile[] {
    const present = new Set(sameOrg.map((u) => u.uid));
    const extra: UserProfile[] = [];
    for (const m of memberships) {
        if (m.orgId !== orgId || present.has(m.uid)) continue;
        const p = profilesByUid[m.uid];
        if (!p) continue;
        present.add(m.uid);
        extra.push({
            ...p,
            role: m.role,
            officeId: m.officeId ?? undefined,
            departmentId: m.departmentId ?? undefined,
            status: m.status,
            crossOrgMember: true,
        });
    }
    return [...sameOrg, ...extra];
}

/**
 * File a switch request and wait for onOrgSwitchRequest's outcome. The caller
 * reloads on success (reloadToHome), so every listener reopens in the new org.
 */
export async function requestOrgSwitch(orgId: string, timeoutMs = 30_000): Promise<{ status: 'success' } | { status: 'error'; errorCode: string }> {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    const ref = await addDoc(collection(db, 'orgSwitchRequests'), {
        uid: user.uid,
        orgId,
        status: 'pending',
        createdAt: serverTimestamp(),
    });
    return new Promise((resolve, reject) => {
        let unsubscribe = () => {};
        const timer = setTimeout(() => {
            unsubscribe();
            reject(new Error('The switch timed out. Try again.'));
        }, timeoutMs);
        unsubscribe = onSnapshot(
            ref,
            (snap) => {
                const data = snap.data();
                if (data && data.status !== 'pending') {
                    clearTimeout(timer);
                    unsubscribe();
                    resolve(data as { status: 'success' } | { status: 'error'; errorCode: string });
                }
            },
            (err) => {
                clearTimeout(timer);
                unsubscribe();
                reject(err);
            },
        );
    });
}
